import { useEffect, useState } from 'react';
import { useRecoilValue } from 'recoil';
import { useWindowSize } from '@Components/Utilities/WindowSize';
import atoms from '@Components/Utilities/SandhillsAtoms';
import { listingTypes } from '@Components/Utilities/SandhillsConstants';
import TranslationText from '@Components/Utilities/TranslationText';
import translator from '@Components/Utilities/Translator';
import { ThemedButton } from '@Components/MuiWrappers/Button';

const ConsignmentHubView = (props) => {
    const routes = useRecoilValue(atoms.routes);

    const windowSize = useWindowSize();
    const [activeSort, setActiveSort] = useState("");
    const [consignedListings, setConsignedListings] = useState(props.ConsignmentListings);
    const [isAscending, setIsAscending] = useState(null);

    useEffect(() => {
        // eslint-disable-next-line react-hooks/immutability
        tableHeaderSortChanged("date");
    }, []);

    const tableHeaderSortChanged = (headerName) => {
        let sortAscending = headerName != activeSort || !isAscending;
        if (isAscending == null) {
            if (props.ListingType.toLowerCase() == listingTypes.AuctionResults.toLowerCase()) {
                sortAscending = false;
            }
            else {
                sortAscending = true;
            }
        }
        
        let sortableObjects = [...consignedListings];
        switch (headerName) {
            case 'date':
                sortableObjects.sort((a, b) => sortNumericValues(new Date(a.AuctionDate).getTime(), new Date(b.AuctionDate).getTime(), sortAscending));
                break;
            case 'highBid':
                sortableObjects.sort((a, b) => sortNumericValues(a.HighBid, b.HighBid, sortAscending));
                break;
            case 'listingType':
                sortableObjects.sort((a, b) => sortValues(a.AuctionListingType?.toLowerCase(), b.AuctionListingType?.toLowerCase(), sortAscending));
                break;
            case 'lotNumber':
                sortableObjects.sort((a, b) => sortValues(a.LotNumber, b.LotNumber, sortAscending, true));
                break;
            case 'lotTitle':
                sortableObjects.sort((a, b) => sortValues(a.DisplayName?.toLowerCase(), b.DisplayName?.toLowerCase(), sortAscending));
                break;
            case 'status':
                sortableObjects.sort((a, b) => sortValues(a.Status?.toLowerCase(), b.Status?.toLowerCase(), sortAscending));
                break;
            default:
                break;
        }

        setActiveSort(headerName);
        setConsignedListings(sortableObjects);
        setIsAscending(sortAscending);
    }

    const sortValues = (valueA, valueB, asc, numeric = false) => {
        if (asc) {
            return valueA && valueA.localeCompare(valueB, undefined, { numeric: numeric, sensitivity: 'base' });
        }
        else {
            return valueA && valueB?.localeCompare(valueA, undefined, { numeric: numeric, sensitivity: 'base' });
        }
    }

    const sortNumericValues = (valueA, valueB, asc) => {
        if (asc) {
            return valueA && !isNaN(valueA) && !isNaN(valueB) && (valueA - valueB);
        }
        else {
            return valueA && !isNaN(valueA) && (valueB - valueA);
        }
    }


    const tableHeader = (headerName, translationKey, className = "") => {
        return (
            <div className={className + ' consignment-table-cell'} role='button' tabIndex='0' onKeyDown={(e) => { if (e.key === "Enter" || e.key === ' ') { e.preventDefault(); tableHeaderSortChanged(headerName); } }} onClick={() => { tableHeaderSortChanged(headerName) }}>
                <TranslationText tKey={translationKey} /><i className={activeSort == headerName ? `fas fa-sort-amount-${isAscending == true ? 'asc' : 'desc'} heavy` : 'fas fa-sort'} />
            </div>);
    }

    const tableHeaders = () => {
        const headers = [];
        headers.push(tableHeader("lotNumber", 'sandhills.auctiontime.api.lotnumber', 'consignment-table-cell-small'));
        headers.push(tableHeader("lotTitle", 'sandhills.lottitle', 'consignment-table-cell-title'));
        headers.push(tableHeader("listingType", 'sandhills.tradepubwebsite.mvc.type', 'consignment-table-cell-icon'));

        if (props.ListingType.toLowerCase() == listingTypes.AuctionResults.toLowerCase()) {
            headers.push(tableHeader("highBid", 'sandhills.highestbid'));
            headers.push(tableHeader("date", 'sandhills.enddate', 'consignment-table-cell-enddate'));
        }
        else {
            headers.push(tableHeader("date", 'sandhills.date'));
            headers.push(tableHeader("status", 'sandhills.status', 'consignment-table-cell-small'));
        }

        headers.push(<div className='consignment-table-header consignment-table-cell-buttons'>&nbsp;</div>);

        return headers;
    }

    const getViewDetailsButtonText = () => {
        var translationKey = "";
        if (props.ListingType.toLowerCase() == listingTypes.AuctionResults.toLowerCase()) {
            translationKey = windowSize.isMobile ? 'sandhills.viewresults' : 'sandhills.results';
        }
        else {
            translationKey = windowSize.isMobile ? 'sandhills.viewdetails' : 'sandhills.tradepubwebsite.mvc.details';
        }
        return <TranslationText tKey={translationKey} />
    }

    return (
        <div className="consignment-hub-container">
            {!windowSize.isMobile &&
                <h2>{translator.translate('sandhills.myconsignments')}</h2>}
            <div>
                <div className="tab-buttons">
                    <a className={props.ListingType.toLowerCase() == listingTypes.UpcomingAuctions.toLowerCase() ? "active" : ""} href={routes.MyConsignmentListings + `?ListingType=${encodeURIComponent(listingTypes.UpcomingAuctions)}`}>
                        {translator.translate('sandhills.tradesites.mvc.myupcomingauctions')}
                    </a>
                    <a className={props.ListingType.toLowerCase() == listingTypes.AuctionResults.toLowerCase() ? "active" : ""} href={routes.MyConsignmentListings + `?ListingType=${encodeURIComponent(listingTypes.AuctionResults)}`}>
                        {translator.translate('sandhills.mypastauctions')}
                    </a>
                </div>
                <div className="consignment-table-container">
                    <div className="consignment-table">
                        {!windowSize.isMobile &&
                            <div className="consignment-table-row consignment-table-headers">
                                {tableHeaders().map((header) => (<>{header}</>))}
                            </div>}
                        {(consignedListings && consignedListings.length > 0) ? (
                            <div>
                                {consignedListings.map((item) => (
                                    <div className="consignment-table-row">
                                        <div className="consignment-table-cell consignment-table-cell-small">
                                            {windowSize.isMobile && <span className="consignment-table-mobile-label"><TranslationText tKey='sandhills.auctiontime.api.lotnumber' /></span>}
                                            {item.LotNumber}
                                        </div>
                                        <div className="consignment-table-cell consignment-table-cell-title">
                                            {item.ListingURL ?
                                                <a href={item.ListingURL} target="_blank" rel="noopener noreferrer">{item.DisplayName}</a>
                                                :
                                                <>{item.DisplayName}</>}
                                        </div>
                                        <div className="consignment-table-cell consignment-table-cell-icon">
                                            <img src={item.AuctionListingTypeLogo} alt={item.AuctionListingType} />
                                        </div>
                                        {props.ListingType.toLowerCase() == listingTypes.AuctionResults.toLowerCase() ?
                                            <>
                                                <div className="consignment-table-cell">
                                                    {windowSize.isMobile && <span className="consignment-table-mobile-label"><TranslationText tKey='sandhills.highestbid' />:</span>}
                                                    <strong className="consignment-table-cell-red">{item.HighBidFormatted}</strong>
                                                </div>
                                                <div className="consignment-table-cell consignment-table-cell-enddate">
                                                    {windowSize.isMobile && <span className="consignment-table-mobile-label"><TranslationText tKey='sandhills.ended' />:</span>}
                                                    {item.AuctionDateFormatted}
                                                </div>
                                            </>
                                            :
                                            <>
                                                <div className="consignment-table-cell consignment-table-cell-date">
                                                    {item.AuctionListingType.toLowerCase() == "pre-auctiontime" ?
                                                        <span className="consignment-table-mobile-label"><TranslationText tKey='sandhills.goestoauction' />:</span>
                                                        :
                                                        <span className="consignment-table-mobile-label"><TranslationText tKey='sandhills.auctionends' />:</span>}
                                                    <span>{item.AuctionDateFormatted}</span>
                                                </div>
                                                <div className={"consignment-table-cell consignment-table-cell-small consignment-table-cell-status" + (item.Status.toUpperCase() == "ACTIVE" || item.Status.toUpperCase() == "PRE-AT" ? " consignment-table-cell-green" : "")}>
                                                    <i className="fa-solid fa-circle"></i>{item.Status.toUpperCase()}
                                                </div>
                                            </>}
                                        <div className="consignment-table-cell consignment-table-cell-buttons">
                                            <div className="consignment-table-buttons">
                                                {item.ConsignmentPackageURL &&
                                                    <a href={item.ConsignmentPackageURL} target="_blank">
                                                        <i className='fa-solid fa-sitemap'></i>
                                                        {windowSize.isMobile ?
                                                            <TranslationText tKey='sandhills.consignmentpackage' />
                                                            : <TranslationText tKey='sandhills.invoice.mvc.package' />}
                                                    </a>}
                                                {item.ListingURL &&
                                                    <a href={item.ListingURL} target="_blank">
                                                        <i className='fa-solid fa-magnifying-glass-plus'></i>
                                                        {getViewDetailsButtonText()}
                                                    </a>}
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <p className="error-message">
                                {translator.translate('sandhills.sandhills.nolistingsfound')}
                            </p>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ConsignmentHubView;